// v5.7.80 — Security Center: MFA enrollment + Active Sessions list.
//
// Why one component, not two?
// Both surfaces share the same trust model (logged-in user managing
// their own credentials) and the same UI shell (the More tab's
// modal/scroll pattern). Splitting them into two pages would force
// users to switch back-and-forth when they're typically there to do
// one auth-hardening task. Single screen, two cards, lazy-load the
// session list only when the user opens the page.
//
// Backup-codes UX note: codes are shown EXACTLY ONCE in a modal with
// a copy-all button. We deliberately do NOT persist them in localStorage
// because that defeats the "save them somewhere safe" instruction.
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  ShieldCheck, ShieldOff, KeyRound, Smartphone, Trash2, Copy,
  ArrowLeft, AlertTriangle, Clock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import ConfirmDialog from '../components/ConfirmDialog';

function timeAgo(iso) {
  if (!iso) return 'unknown';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function SecurityCenter({ onBack }) {
  // ─── MFA state ────────────────────────────────────────────────────────────
  const [mfaStatus, setMfaStatus] = useState(null);   // {enabled, has_backup_codes}
  const [showSetup, setShowSetup] = useState(false);  // 1st modal: password + secret
  const [setupPwd, setSetupPwd] = useState('');
  const [setupData, setSetupData] = useState(null);   // {secret, otpauth_uri, ...}
  const [setupCode, setSetupCode] = useState('');     // 6-digit confirmation
  const [showCodes, setShowCodes] = useState(null);   // string[] backup codes
  // v5.7.80 P2 — Over-shoulder defence: hide the raw base32 secret
  // behind a "Show" toggle. QR is harder to capture (needs a camera
  // aim) than text, so the secret is the higher-risk visual.
  const [showRawSecret, setShowRawSecret] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [disablePwd, setDisablePwd] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showRegen, setShowRegen] = useState(false);
  const [regenPwd, setRegenPwd] = useState('');
  const [busy, setBusy] = useState(false);

  // ─── Sessions state ───────────────────────────────────────────────────────
  const [sessions, setSessions] = useState(null);     // [{family_id, device_label, ...}]
  const [revokeTarget, setRevokeTarget] = useState(null);  // family_id pending confirm

  const refreshMfa = async () => {
    try {
      const r = await api.get('/auth/mfa/status');
      setMfaStatus(r.data);
    } catch (e) {
      // 401 falls through to AuthContext handling — anything else we want
      // visible in the console so an MFA endpoint regression is debuggable.
      console.warn('[SecurityCenter] mfa/status fetch failed:', e?.message || e);
    }
  };
  const refreshSessions = async () => {
    try {
      const r = await api.get('/auth/sessions');
      setSessions(r.data.sessions);
    } catch {
      setSessions([]);
    }
  };

  useEffect(() => {
    refreshMfa();
    refreshSessions();
  }, []);

  // ─── MFA: setup → confirm → done ──────────────────────────────────────────
  const doSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post('/auth/mfa/setup', { password: setupPwd });
      setSetupData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wrong password');
    } finally {
      setBusy(false);
    }
  };

  const doVerifySetup = async () => {
    setBusy(true);
    try {
      const r = await api.post('/auth/mfa/verify-setup', { code: setupCode });
      setShowSetup(false);
      setSetupPwd('');
      setSetupCode('');
      setSetupData(null);
      setShowCodes(r.data.backup_codes);  // open backup-codes modal
      await refreshMfa();
      toast.success('MFA enabled');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const doDisable = async () => {
    setBusy(true);
    try {
      await api.post('/auth/mfa/disable', { password: disablePwd, code: disableCode });
      setShowDisable(false);
      setDisablePwd('');
      setDisableCode('');
      await refreshMfa();
      toast.success('MFA disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setBusy(false);
    }
  };

  const doRegen = async () => {
    setBusy(true);
    try {
      const r = await api.post('/auth/mfa/backup-codes/regenerate', { password: regenPwd });
      setShowRegen(false);
      setRegenPwd('');
      setShowCodes(r.data.backup_codes);
      await refreshMfa();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  // ─── Sessions: revoke ─────────────────────────────────────────────────────
  const doRevoke = async () => {
    if (!revokeTarget) return;
    setBusy(true);
    try {
      await api.delete(`/auth/sessions/${revokeTarget}`);
      setRevokeTarget(null);
      await refreshSessions();
      toast.success('Session revoked');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to revoke session');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-background" data-testid="security-center">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border/60 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="security-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-lg font-semibold">Security</h1>
      </div>

      <ScrollArea className="flex-1 px-4 py-4">
        {/* ───── MFA Card ──────────────────────────────────────────────────── */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Two-Factor Authentication
            </h3>
          </div>
          <div className="bg-card border border-border/60 rounded-2xl p-4">
            {mfaStatus === null ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : mfaStatus.enabled ? (
              <div className="space-y-3" data-testid="mfa-enabled-panel">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-6 w-6 text-emerald-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold">MFA enabled</p>
                    <p className="text-xs text-muted-foreground">
                      {mfaStatus.has_backup_codes
                        ? 'Backup codes ready'
                        : 'No backup codes — generate some'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setShowRegen(true)}
                    data-testid="mfa-regen-btn"
                  >
                    <KeyRound className="h-4 w-4 mr-1" /> Regenerate backup codes
                  </Button>
                  <Button
                    size="sm" variant="destructive"
                    onClick={() => setShowDisable(true)}
                    data-testid="mfa-disable-btn"
                  >
                    <ShieldOff className="h-4 w-4 mr-1" /> Disable
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="mfa-disabled-panel">
                <div className="flex items-start gap-3">
                  <ShieldOff className="h-6 w-6 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold">MFA not enabled</p>
                    <p className="text-xs text-muted-foreground">
                      Protect your account with a 6-digit code from an authenticator app
                      (Google Authenticator, 1Password, Authy, Apple Passwords, …).
                    </p>
                  </div>
                </div>
                <Button onClick={() => setShowSetup(true)} data-testid="mfa-enable-btn">
                  <ShieldCheck className="h-4 w-4 mr-1" /> Enable MFA
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* ───── Active Sessions Card ──────────────────────────────────────── */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Smartphone className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Active Sessions
            </h3>
          </div>
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            {sessions === null && (
              <p className="text-sm text-muted-foreground p-4">Loading…</p>
            )}
            {sessions !== null && sessions.length === 0 && (
              <p className="text-sm text-muted-foreground p-4">No active sessions.</p>
            )}
            {sessions !== null && sessions.map((s) => (
              <div
                key={s.family_id}
                className="flex items-start gap-3 p-4 border-b border-border/40 last:border-b-0"
                data-testid={`session-row-${s.family_id}`}
              >
                <Smartphone className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{s.device_label || 'Unknown device'}</span>
                    {s.is_current && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500 font-semibold uppercase">
                        This device
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {s.ip || 'unknown IP'}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> last used {timeAgo(s.last_used_at)}
                  </p>
                </div>
                {!s.is_current && (
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => setRevokeTarget(s.family_id)}
                    aria-label="Revoke session"
                    data-testid={`session-revoke-${s.family_id}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </ScrollArea>

      {/* ───── Setup Modal ─────────────────────────────────────────────────── */}
      <Dialog open={showSetup} onOpenChange={(o) => { if (!o) { setShowSetup(false); setSetupData(null); setSetupPwd(''); setSetupCode(''); setShowRawSecret(false); } }}>
        <DialogContent className="max-w-md" data-testid="mfa-setup-dialog">
          <DialogHeader>
            <DialogTitle>{setupData ? 'Add to your authenticator' : 'Confirm your password'}</DialogTitle>
          </DialogHeader>
          {!setupData ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Re-enter your password to enable MFA on your account.
              </p>
              <Input
                type="password"
                placeholder="Password"
                value={setupPwd}
                onChange={(e) => setSetupPwd(e.target.value)}
                data-testid="mfa-setup-password-input"
              />
              <DialogFooter>
                <Button onClick={doSetup} disabled={busy || !setupPwd} data-testid="mfa-setup-continue-btn">
                  Continue
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Scan this QR code with your authenticator app, or paste the secret below.
              </p>
              <div className="bg-white p-3 rounded-lg flex justify-center">
                {/* H-1 (2026-01): QR rendered server-side and delivered as a
                    data: URI so the secret-bearing otpauth_uri never leaves
                    our backend to a third-party QR service. Falls back to the
                    manual base32 secret below if the server couldn't render. */}
                {setupData.qr_png ? (
                  <img
                    alt="MFA QR"
                    src={setupData.qr_png}
                    width="240" height="240"
                    data-testid="mfa-setup-qr"
                  />
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-8" data-testid="mfa-setup-qr-fallback">
                    QR unavailable — enter the secret below into your authenticator app manually.
                  </p>
                )}
              </div>
              <div className="bg-muted px-3 py-2 rounded font-mono text-xs break-all flex items-center justify-between gap-2">
                <span data-testid="mfa-setup-secret">
                  {showRawSecret ? setupData.secret : '•'.repeat(32)}
                </span>
                <button
                  type="button"
                  className="text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground shrink-0"
                  onClick={() => setShowRawSecret(!showRawSecret)}
                  data-testid="mfa-setup-toggle-secret"
                >
                  {showRawSecret ? 'Hide' : 'Show'}
                </button>
              </div>
              <Label htmlFor="mfa-setup-code">Enter the 6-digit code from your app</Label>
              <Input
                id="mfa-setup-code"
                placeholder="123456"
                inputMode="numeric"
                maxLength={6}
                value={setupCode}
                onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="mfa-setup-code-input"
              />
              <DialogFooter>
                <Button onClick={doVerifySetup} disabled={busy || setupCode.length !== 6} data-testid="mfa-setup-verify-btn">
                  Verify & enable
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ───── Backup Codes Modal (shown ONCE post-enable + post-regen) ───── */}
      <Dialog open={!!showCodes} onOpenChange={(o) => { if (!o) setShowCodes(null); }}>
        <DialogContent className="max-w-md" data-testid="mfa-backup-codes-dialog">
          <DialogHeader>
            <DialogTitle>Save your backup codes</DialogTitle>
          </DialogHeader>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-xs">
              <strong>These codes will NEVER be shown again.</strong> Save them in a password manager or
              print them — they let you sign in if you lose your authenticator device.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {(showCodes || []).map((c) => (
              <div key={c} className="bg-muted px-2 py-1.5 rounded text-center" data-testid={`backup-code-${c}`}>{c}</div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { navigator.clipboard.writeText((showCodes || []).join('\n')); toast.success('Copied'); }}
              data-testid="mfa-copy-codes-btn"
            >
              <Copy className="h-4 w-4 mr-1" /> Copy all
            </Button>
            <Button onClick={() => setShowCodes(null)} data-testid="mfa-codes-done-btn">I saved them</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───── Disable Modal ───────────────────────────────────────────────── */}
      <Dialog open={showDisable} onOpenChange={(o) => { if (!o) { setShowDisable(false); setDisablePwd(''); setDisableCode(''); } }}>
        <DialogContent className="max-w-md" data-testid="mfa-disable-dialog">
          <DialogHeader>
            <DialogTitle>Disable MFA</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-3">
            Confirm with your password AND a current MFA code (or a backup code).
          </p>
          <div className="space-y-3">
            <Input type="password" placeholder="Password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)} data-testid="mfa-disable-password-input" />
            <Input placeholder="6-digit code or backup code" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} data-testid="mfa-disable-code-input" />
          </div>
          <DialogFooter>
            <Button variant="destructive" onClick={doDisable} disabled={busy || !disablePwd || !disableCode} data-testid="mfa-disable-confirm-btn">
              Disable MFA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───── Regenerate Codes Modal ──────────────────────────────────────── */}
      <Dialog open={showRegen} onOpenChange={(o) => { if (!o) { setShowRegen(false); setRegenPwd(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Regenerate backup codes</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-3">
            Old backup codes will stop working immediately. Confirm with your password.
          </p>
          <Input type="password" placeholder="Password" value={regenPwd} onChange={(e) => setRegenPwd(e.target.value)} />
          <DialogFooter>
            <Button onClick={doRegen} disabled={busy || !regenPwd}>Regenerate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke this session?"
        description="The signed-in device will be logged out the next time it tries to refresh its session. This is recommended if you don't recognize the device."
        confirmLabel="Revoke"
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={doRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
}
